// Motion tokens for the v2 layer.
//
// Companion to tokens.ts, bound by the same rule: if a name sounds generic
// (`fast`, `medium`, `transition2`), it's wrong — it should name the physical
// behaviour it produces. A `transport` easing is the tape mechanism spinning
// up and locking; a `punch` is a stamp hitting paper.
//
// Durations are SECONDS because that is framer-motion's unit and the vast
// majority of consumers hand them straight to a `transition` prop. The one
// place milliseconds are needed (setTimeout scheduling in the transition
// orchestrator) converts explicitly via `ms()`.
//
// Before this file existed there were 18 hard-coded duration literals across
// the first-screen route files alone, every one of them `ease: 'easeOut'`.
// New motion work reaches for these names instead.

/** Seconds. */
export const duration = {
  /** Press feedback. Must land inside the ~100ms window where the eye still
      reads it as "the thing I touched responded", not "the thing animated". */
  tap: 0.09,
  /** Hover tints, border warms, glyph colour shifts. */
  hint: 0.16,
  /** Default for an element arriving or leaving. */
  enter: 0.3,
  /** An element arriving with weight — modals, panels, anything with mass. */
  settle: 0.5,
  /** One beat of an authored sequence. */
  beat: 0.8,
  /** The full Welcome → Menu tape shuttle, end to end. */
  transport: 1.52,
} as const;

// Cubic-bezier control points. framer-motion accepts these as a 4-tuple.
// Kept as plain arrays (not `as const` tuples) so they satisfy framer's
// `number[]` easing signature without a cast at every call site.
export const ease: Record<string, number[]> = {
  /** Standard decelerate — the default for anything arriving. */
  out: [0.22, 1, 0.36, 1],
  /** Accelerate away — anything leaving frame. */
  in: [0.64, 0, 0.78, 0],
  /** Symmetric. Ambient drift, breathing loops. */
  inOut: [0.65, 0, 0.35, 1],
  /**
   * The tape transport. Heavy S-curve: slow to overcome inertia, fast through
   * the middle, hard lock at the end. This is the signature easing of the
   * Welcome → Menu sequence and should not be used for ordinary UI.
   */
  transport: [0.83, 0, 0.17, 1],
  /** Overshoot. Stamps, impacts, the `vs.` mark landing. */
  punch: [0.34, 1.56, 0.64, 1],
};

/**
 * Easing as plain functions, for motion we drive ourselves.
 *
 * The `ease` arrays above are for framer's declarative `transition` prop.
 * These are the same curves as callable math, used by anything that owns its
 * own clock — chiefly the tape transport, which interpolates a scored timeline
 * on a single requestAnimationFrame loop rather than handing keyframes to a
 * scheduler. Each takes and returns 0..1.
 */
export const easeFn: Record<string, (x: number) => number> = {
  linear: (x) => x,
  /** Decelerate. Mirrors `ease.out`. */
  out: (x) => 1 - Math.pow(1 - x, 3),
  /** Accelerate. Mirrors `ease.in`. */
  in: (x) => x * x * x,
  inOut: (x) => (x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2),
  /**
   * The tape transport: heavier S-curve than inOut. Slow to overcome inertia,
   * quick through the middle, hard lock at the end.
   */
  transport: (x) => (x < 0.5 ? 8 * x * x * x * x : 1 - Math.pow(-2 * x + 2, 4) / 2),
  /** Overshoot, for stamps and impacts. */
  punch: (x) => {
    const c1 = 1.70158;
    const c3 = c1 + 1;
    return 1 + c3 * Math.pow(x - 1, 3) + c1 * Math.pow(x - 1, 2);
  },
};

// Spring presets. Typed loosely for the same reason as `ease` — framer's
// Transition union is wider than what a `as const` literal infers to.
export const spring = {
  /** Snappy, barely any overshoot. Tiles, chips, small UI. */
  stiff: { type: 'spring' as const, stiffness: 520, damping: 34, mass: 0.7 },
  /** Noticeable settle. Cards, panels. */
  soft: { type: 'spring' as const, stiffness: 260, damping: 26, mass: 0.9 },
  /** Heavy mechanical settle — the cassette seating into the deck. */
  deck: { type: 'spring' as const, stiffness: 180, damping: 22, mass: 1.4 },
};

/** Interval between siblings in a staggered group, in seconds. */
export const stagger = {
  tight: 0.04,
  base: 0.07,
  loose: 0.12,
} as const;

/** Seconds → milliseconds, for setTimeout-based orchestration. */
export function ms(seconds: number): number {
  return Math.round(seconds * 1000);
}

/**
 * The three-way motion branch. Lives here rather than in the hook that
 * resolves it so pure, non-React modules (timelines, tests running in the
 * node environment) can depend on the type without pulling in React.
 *
 * See `src/components/useMotionTier.ts` for what each tier means and how it
 * is resolved.
 */
export type MotionTier = 'full' | 'lowPower' | 'reduced';
